import { APP_CONFIG } from "app.config";
import { MatxLoading } from "app/components";
import httpClient from "httpClient";
import jwtDecode from "jwt-decode";
import { createContext, useEffect, useReducer } from "react";

const initialState = {
  isAuthenticated: false,
  loggedOut: false,
  isInitialised: false,
  message: "",
  userId: "",
  username: "",
  roleId: "",
  roleName: "",
  permissions: [],
  fullName: "",
  designation: "",
  zoneId: "",
  zoneName: "",
  areaId: "",
  areaName: "",
};

const isValidToken = (accessToken) => {
  if (!accessToken) {
    return false;
  }

  const decodedToken = jwtDecode(accessToken);
  const currentTime = Date.now() / 1000;
  if (decodedToken.exp <= currentTime) {
    return false;
  }
  return Boolean(decodedToken.Username && decodedToken.RoleId);
};

const setSession = (accessToken) => {
  if (accessToken) {
    localStorage.setItem("accessToken", accessToken);
    httpClient.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem("accessToken");
    delete httpClient.defaults.headers.common.Authorization;
  }
};

const reducer = (state, action) => {
  switch (action.type) {
    case "INIT": {
      const {
        isAuthenticated,
        message,
        userId,
        username,
        roleId,
        roleName,
        permissions,
        fullName,
        designation,
        zoneId,
        zoneName,
        areaId,
        areaName,
      } = action.payload;

      return {
        ...state,
        isAuthenticated,
        isInitialised: true,
        message,
        userId: userId,
        username: username,
        roleId: roleId,
        roleName: roleName,
        permissions: permissions,
        fullName,
        designation,
        zoneId,
        zoneName,
        areaId,
        areaName,
      };
    }
    case "LOGIN": {
      const {
        message,
        userId,
        username,
        roleId,
        roleName,
        permissions,
        fullName,
        designation,
        zoneId,
        zoneName,
        areaId,
        areaName,
      } = action.payload;

      return {
        ...state,
        isAuthenticated: true,
        message,
        userId: userId,
        username: username,
        roleId: roleId,
        roleName: roleName,
        permissions: permissions,
        fullName,
        designation,
        zoneId,
        zoneName,
        areaId,
        areaName,
        loggedOut: false,
      };
    }
    case "LOGOUT": {
      return {
        ...state,
        isAuthenticated: false,
        message: "",
        loggedOut: true,
      };
    }
    case "REGISTER": {
      return {
        ...state,
      };
    }

    default: {
      return { ...state };
    }
  }
};

const AuthContext = createContext({
  ...initialState,
  method: "JWT",
  login: () => Promise.resolve(),
  logout: () => {},
  register: () => Promise.resolve(),
});

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const login = async (username, password) => {
    const response = await httpClient.post(
      APP_CONFIG.BASE_API + "/Auth/Login",
      {
        username,
        password,
      }
    );
    const { data, message, succeeded } = response.data;

    if (!succeeded) {
      throw new Error(message || "Login failed.");
    }

    const decodedToken = jwtDecode(data);
    setSession(data);

    dispatch({
      type: "LOGIN",
      payload: {
        message,
        userId: decodedToken?.UserId,
        username: decodedToken?.Username,
        roleId: decodedToken?.RoleId,
        roleName: decodedToken?.RoleName,
        permissions: decodedToken["Permission"],
        fullName: decodedToken?.FullName,
        designation: decodedToken?.DesignationName,
        zoneId: decodedToken?.ZoneId,
        zoneName: decodedToken?.ZoneName,
        areaId: decodedToken?.AreaId,
        areaName: decodedToken?.AreaName,
      },
    });

    return decodedToken["Permission"];
  };
  const register = async (
    firstName,
    lastName,
    jobId,
    designation,
    zone,
    username,
    password
  ) => {
    const response = await httpClient.post(APP_CONFIG.BASE_API + "/User", {
      firstName,
      lastName,
      jobId,
      designation,
      zone,
      username,
      password,
    });

    const { id } = response.data;

    dispatch({
      type: "REGISTER",
      payload: {
        id,
      },
    });
  };
  const logout = () => {
    setSession(null);
    dispatch({ type: "LOGOUT" });
  };

  useEffect(() => {
    (async () => {
      try {
        const accessToken = window.localStorage.getItem("accessToken");

        if (accessToken && isValidToken(accessToken)) {
          setSession(accessToken);
          const decodedToken = jwtDecode(accessToken);

          dispatch({
            type: "INIT",
            payload: {
              isAuthenticated: true,
              userId: decodedToken?.UserId,
              username: decodedToken?.Username,
              roleId: decodedToken?.RoleId,
              roleName: decodedToken?.RoleName,
              permissions: decodedToken["Permission"],
              fullName: decodedToken?.FullName,
              designation: decodedToken?.DesignationName,
              zoneId: decodedToken?.ZoneId,
              zoneName: decodedToken?.ZoneName,
              areaId: decodedToken?.AreaId,
              areaName: decodedToken?.AreaName,
            },
          });
        } else {
          dispatch({
            type: "INIT",
            payload: {
              isAuthenticated: false,
              userId: null,
              username: null,
              roleId: null,
              roleName: null,
              fullName: null,
              designation: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: "INIT",
          payload: {
            isAuthenticated: false,
            userId: null,
            username: null,
            roleId: null,
            roleName: null,
            fullName: null,
            designation: null,
          },
        });
      }
    })();
  }, []);

  if (!state.isInitialised) {
    return <MatxLoading />;
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: "JWT",
        login,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// AuthProvider.propTypes = {
//   children: PropTypes.node.isRequired,
// };

export default AuthContext;
